import type {
  Baseline,
  IElement,
  OneOfChildElements,
  OneOfElements,
} from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import type { ItemOrItemId } from '@shared/models/type-helpers.model';
import { getChangeAffectedElementId } from './action.helpers';
import { projectGuards } from '../guards/project-guards.helpers';
import { compare } from '../compare.helpers';
import { flattenElements } from '../recursive-element.helpers';
import {
  type Action,
  type ProjectChange,
  type ProjectChangeMap,
  ProjectChangeType,
} from '../pipeline/update-engine.models';
import {
  collectProposalChangesInOrder,
  getProposal,
} from '../proposals/proposals.helpers';

type ElementRecipeApplyChange =
  ProjectChangeMap[ProjectChangeType.ElementRecipeApply];

const isRecipeApply = (
  change: ProjectChange,
): change is ElementRecipeApplyChange =>
  change.type === ProjectChangeType.ElementRecipeApply;

/** Map of `elementId → resolvedElement` for the recipe-apply changes used by a single proposal. */
type RecipeResolvedElementMap = Map<string, IElement>;

type RecipeResolvedElementLookup = {
  /** Cached subtree per element id (only for the latest recipe.apply that has `resolvedElement` set). */
  lookup: RecipeResolvedElementMap;
  /**
   * Number of recipe.apply targets that cannot use the cache: missing `resolvedElement`,
   * or the cache no longer matches the post-{@link resolveVersionForProposal} subtree
   * (e.g. a later proposal property edit on the recipe target).
   */
  uncachedCount: number;
};

/**
 * Build the lookup of `elementId → resolvedElement` for every {@link ElementRecipeApplyChange}
 * in the proposal's referenced actions.
 *
 * Iterates actions in proposal order and tracks the LATEST recipe.apply per `elementId`
 * (mirrors `last recipe.apply wins` condense semantics across action boundaries). Only the
 * latest change's `resolvedElement` participates in the lookup; if that latest change is
 * uncached, the element is counted in `uncachedCount` and not substituted — so an older
 * cached value can't leak through after a newer overwrite.
 *
 * A cached `resolvedElement` is only used when no later proposal change (in action order)
 * targets the recipe root or any id inside the cached subtree — otherwise the target is
 * uncached so post-resolve edits are not overwritten by a stale cache.
 */
/**
 * True when a proposal change after the latest `recipe.apply` for {@link elementId} affects
 * the recipe target or any node in {@link cachedSubtree}.
 */
const hasLaterProposalChangesAffectingCachedSubtree = (
  orderedChanges: ProjectChange[],
  elementId: string,
  cachedSubtree: IElement,
): boolean => {
  const subtreeIds = new Set(
    flattenElements(cachedSubtree).map((node) => node.id),
  );
  let lastRecipeApplyIndex = -1;
  for (const [i, change] of orderedChanges.entries()) {
    if (isRecipeApply(change) && change.elementId === elementId) {
      lastRecipeApplyIndex = i;
    }
  }
  if (lastRecipeApplyIndex < 0) return true;

  for (let i = lastRecipeApplyIndex + 1; i < orderedChanges.length; i++) {
    const change = orderedChanges[i];
    if (!change) continue;
    if (isRecipeApply(change) && change.elementId === elementId) continue;
    const affectedId = getChangeAffectedElementId(change);
    if (!affectedId) continue;
    if (affectedId === elementId || subtreeIds.has(affectedId)) return true;
  }
  return false;
};

const buildRecipeResolvedElementLookup = (
  version: Baseline,
  proposal: IProposal,
): RecipeResolvedElementLookup => {
  const lookup: RecipeResolvedElementMap = new Map();
  const actions = version.actions;
  if (!actions?.length || proposal.actionIds.length === 0) {
    return { lookup, uncachedCount: 0 };
  }

  const latestByElementId = new Map<
    string,
    ProjectChangeMap[ProjectChangeType.ElementRecipeApply]
  >();
  const actionsById = new Map<string, Action>(actions.map((a) => [a.id, a]));
  for (const actionId of proposal.actionIds) {
    const action = actionsById.get(actionId);
    if (!action) continue;
    for (const change of action.changes) {
      if (!isRecipeApply(change)) continue;
      latestByElementId.set(change.elementId, change);
    }
  }

  const orderedChanges = collectProposalChangesInOrder(version, proposal);

  let uncachedCount = 0;
  for (const [elementId, change] of latestByElementId) {
    const cached = change.resolvedElement;
    if (!cached) {
      uncachedCount++;
      continue;
    }
    if (
      hasLaterProposalChangesAffectingCachedSubtree(
        orderedChanges,
        elementId,
        cached,
      )
    ) {
      uncachedCount++;
      continue;
    }
    lookup.set(elementId, cached);
  }
  return { lookup, uncachedCount };
};

/**
 * Result of {@link substituteCachedRecipeElementsForProposal}: the (possibly new)
 * version reference and the set of root element ids whose subtree was replaced
 * by a cached `resolvedElement`. Callers feed `substitutedIds` into
 * {@link enrichResolvedTree} via `skipSubtreeIds` so the enrich walk doesn't
 * redo the work that produced the cached subtree.
 *
 * `hasUncachedRecipeApply` is `true` when at least one recipe.apply in the proposal
 * lacks a cached `resolvedElement` — only then does {@link enrichResolvedTree} need
 * to run to compute fresh recipe child quantities. When `false`, callers can skip
 * the enrich walk entirely (the heavy work is already baked into the cache).
 */
export type SubstituteCachedRecipeElementsResult = {
  version: Baseline;
  substitutedIds: ReadonlySet<string>;
  hasUncachedRecipeApply: boolean;
};

/**
 * Recursively replace the subtree at any element whose id is in {@link lookup}
 * with the cached {@link IElement}. Returns the original reference when no
 * substitutions occurred.
 */
const substituteChildren = (
  children: OneOfChildElements[],
  lookup: RecipeResolvedElementMap,
  substitutedIds: Set<string>,
): OneOfChildElements[] => {
  let changed = false;
  const next = children.map((child) => {
    const replaced = substituteElement(child, lookup, substitutedIds);
    if (replaced !== child) changed = true;
    return replaced;
  });
  return changed ? (next as typeof children) : children;
};

/**
 * Replace {@link element} with {@link cached} when ids match; records the id for
 * {@link enrichResolvedTree} `skipSubtreeIds`.
 */
const substituteWithCachedElement = (
  element: IElement,
  cached: IElement,
  substitutedIds: Set<string>,
): IElement => {
  substitutedIds.add(element.id);
  return cached;
};

const substituteElement = (
  element: OneOfElements,
  lookup: RecipeResolvedElementMap,
  substitutedIds: Set<string>,
): OneOfElements => {
  if (!projectGuards.is.element(element)) return element;
  const cached = lookup.get(element.id);
  if (cached && projectGuards.is.element(cached)) {
    return substituteWithCachedElement(element, cached, substitutedIds);
  }
  const nextChildren = substituteChildren(
    element.elements as OneOfChildElements[],
    lookup,
    substitutedIds,
  );
  if (nextChildren === element.elements) return element;
  return { ...element, elements: nextChildren as typeof element.elements };
};

/**
 * Walk the proposal's recipe-apply lookup and replace every matching subtree on
 * {@link version} with the cached {@link IElement}. Used by
 * {@link resolveAndEnrichVersionForProposal} to skip the expensive enrich walk
 * for recipe-applied subtrees on proposal switches.
 *
 * @param version - Resolved (post-`resolveVersionForProposal`) version
 * @param sourceVersion - Original version whose actions hold the cache
 * @param proposalOrId - Proposal whose actions provide the cached elements
 * @returns Substituted version and the set of substituted root element ids
 */
export const substituteCachedRecipeElementsForProposal = (
  version: Baseline,
  sourceVersion: Baseline,
  proposalOrId: ItemOrItemId<IProposal> | undefined,
): SubstituteCachedRecipeElementsResult => {
  const proposal = getProposal(sourceVersion, proposalOrId);
  if (!proposal) {
    return {
      version,
      substitutedIds: new Set(),
      hasUncachedRecipeApply: false,
    };
  }

  const { lookup, uncachedCount } = buildRecipeResolvedElementLookup(
    sourceVersion,
    proposal,
  );
  const hasUncachedRecipeApply = uncachedCount > 0;
  if (lookup.size === 0) {
    return { version, substitutedIds: new Set(), hasUncachedRecipeApply };
  }

  const substitutedIds = new Set<string>();
  const nextElements = substituteChildren(
    version.elements,
    lookup,
    substitutedIds,
  );
  if (nextElements === version.elements) {
    return { version, substitutedIds, hasUncachedRecipeApply };
  }
  return {
    version: { ...version, elements: nextElements },
    substitutedIds,
    hasUncachedRecipeApply,
  };
};

/**
 * Update {@link ElementRecipeApplyChange.resolvedElement} on every recipe.apply change
 * in {@link Baseline.actions} based on the freshly-enriched view per proposal.
 *
 * @param version - Current version
 * @param freshElementByActionRecipeKey - Map keyed by `${actionId}:${elementId}` →
 *   freshly-resolved {@link IElement} for the recipe.apply target. Pass `undefined`
 *   to clear the cache.
 * @returns New version reference when any action's changes were updated; otherwise
 *   the original reference.
 */
export const writeBackRecipeResolvedElements = (
  version: Baseline,
  freshElementByActionRecipeKey: Map<string, IElement | undefined>,
): Baseline => {
  const actions = version.actions;
  if (!actions?.length) return version;

  let actionsChanged = false;
  const nextActions = actions.map((action) => {
    let changesChanged = false;
    const nextChanges = action.changes.map((change): ProjectChange => {
      if (!isRecipeApply(change)) return change;
      const key = `${action.id}:${change.elementId}`;
      const fresh = freshElementByActionRecipeKey.get(key);
      const same = compare.equal.deep(change.resolvedElement, fresh);
      if (same) return change;
      changesChanged = true;
      // Drop the field entirely when fresh is undefined (e.g. recipe target no longer in tree).
      if (fresh === undefined) {
        const { resolvedElement: _omit, ...rest } = change;
        return rest as ElementRecipeApplyChange;
      }
      return { ...change, resolvedElement: fresh };
    });
    if (!changesChanged) return action;
    actionsChanged = true;
    return { ...action, changes: nextChanges };
  });
  if (!actionsChanged) return version;
  return { ...version, actions: nextActions };
};

/**
 * Strip {@link ElementRecipeApplyChange.resolvedElement} from a list of changes.
 * Used by clone / regenerate flows where the cached subtree wouldn't apply to the
 * cloned ids.
 */
export const stripRecipeResolvedElement = (
  changes: ProjectChange[],
): ProjectChange[] => {
  let changed = false;
  const next = changes.map((change): ProjectChange => {
    if (!isRecipeApply(change) || change.resolvedElement === undefined) {
      return change;
    }
    changed = true;
    const { resolvedElement: _omit, ...rest } = change;
    return rest as ElementRecipeApplyChange;
  });
  return changed ? next : changes;
};
